import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';

import ProjectsPaywall from '@components/ProjectsPaywall';
import EnhancedPaywall from '@components/ProjectsPaywall/EnhancedPaywall';
import useEventTracking from '@hooks/useEventTracking';
import useIframeResizeMessager from '@hooks/useIframeResizeMessager';
import { HTMLViaNotesProps } from '@interfaces/projects';
import { project_path } from '@routes';
import { ClickTarget, Page, click } from '@tracking';
import { AuthoredContentReactToHtmlParser } from '@utils/reactHtmlParserTransform';

import TopSection from './TopSection';

const ANCHOR_TAG = 'a';
export const AI_INDEX_PROJECT_SLUG = 'aiindex';

const HTMLViaNotes = ({
  data,
  paywallData,
  project,
  projectMedia,
  unlocked,
}: HTMLViaNotesProps): JSX.Element => {
  const projectPageRef = useRef<HTMLDivElement>(null);
  useIframeResizeMessager();
  const projectImagePath = project_path(project.slug);
  const { Track, trackEvent } = useEventTracking({
    location: Page.Project,
    contentId: project.id,
  });

  useEffect(() => {
    const projectElement = projectPageRef.current;

    if (!projectElement || !trackEvent) return;

    function handleProjectClick(this: HTMLElement, event: MouseEvent) {
      const link = (event.target as HTMLElement).closest(ANCHOR_TAG);
      if (link) {
        trackEvent(
          click({
            clickTarget: ClickTarget.AnchorLink,
            clickLink: link.href,
            clickText: link.innerText,
          })
        );
      }
    }

    projectElement.addEventListener('click', handleProjectClick, { capture: true, passive: true });

    return () => {
      projectElement.removeEventListener('click', handleProjectClick, {
        capture: true,
        passive: true,
      } as EventListenerOptions);
    };
  }, [trackEvent]);

  return (
    <Track>
      <main className="w-full px-16 sm:px-28" ref={projectPageRef}>
        <div
          className={clsx({
            "pointer-events-none relative select-none after:absolute after:-inset-x-16 after:-top-90 after:bottom-0 after:bg-black/30 after:content-[''] sm:after:-inset-x-28 sm:after:-top-[140px]":
              !unlocked,
          })}
        >
          <TopSection
            accessLevel={project.accessLevel}
            authors={project.authors}
            description={project.description}
            mediaSourcePath={projectImagePath}
            projectMedia={projectMedia}
            slug={project.slug}
            title={project.title}
            titleCallout={project.titleCallout}
            updatedAt={project.updatedAt}
            unlocked={unlocked}
          />
        </div>
        {!unlocked ? (
          <>
            <div
              className={clsx(
                'relative before:absolute before:bottom-0 before:left-0 before:z-10 before:h-[300px] before:w-full before:bg-[linear-gradient(to_top,#fff_0%,rgba(255,255,255,0)_100%)] dark:before:bg-[linear-gradient(to_top,#1a1a1a_0%,rgba(26,26,26,0)_100%)]',
                { '-mt-[48px]': project.slug === AI_INDEX_PROJECT_SLUG }
              )}
            ></div>
            <div className="sticky bottom-0 z-50">
              {project.slug === AI_INDEX_PROJECT_SLUG ? (
                <EnhancedPaywall
                  leadType={project.leadType}
                  imagePath={paywallData.imagePath || ''}
                  features={paywallData.features || []}
                  title={paywallData.title || ''}
                  subtitle={paywallData.subtitle || ''}
                  buttonText={paywallData.buttonText || ''}
                  offerLink={paywallData.offerLink || ''}
                  isPro
                  isCurrentUserPresent={paywallData.isLoggedIn}
                  projectSlug={project.slug}
                />
              ) : (
                <ProjectsPaywall {...paywallData} />
              )}
            </div>
          </>
        ) : (
          <div className="mx-auto mt-32 w-full max-w-container-lg sm:mt-48">
            <span id="no-paywall"></span>
            <div className="mx-auto w-full">{data && AuthoredContentReactToHtmlParser(data)}</div>
          </div>
        )}
      </main>
    </Track>
  );
};

export default HTMLViaNotes;
